import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useStore } from '../../lib/store';
import toast from 'react-hot-toast';

interface CombinedResult {
  result_type: 'lab' | 'radiology';
  id: string;
  report_no: string;
  patient_id: string;
  patient_name: string;
  doctor_id: string;
  created_at: string;
  status: string;
  priority: string;
  finalized_at: string;
  pdf_url: string;
  modality?: string;
  tests: string;
}

export default function DoctorResults() {
  const { user } = useStore();
  const [results, setResults] = useState<CombinedResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<CombinedResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'lab' | 'radiology'>('all');
  const [filterPatient, setFilterPatient] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadResults();
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [results, filterType, filterPatient, startDate, endDate]);

  const loadResults = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('doctor_patient_results_v')
        .select('*')
        .eq('doctor_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResults(data || []);
    } catch (error) {
      console.error('Failed to load results:', error);
      toast.error('Failed to load patient results');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = results;

    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.result_type === filterType);
    }

    if (filterPatient) {
      filtered = filtered.filter(r =>
        r.patient_name.toLowerCase().includes(filterPatient.toLowerCase())
      );
    }

    if (startDate) {
      filtered = filtered.filter(r => r.created_at >= startDate);
    }

    if (endDate) {
      filtered = filtered.filter(r => r.created_at <= endDate);
    }

    setFilteredResults(filtered);
  };

  const getSignedUrl = async (pdfUrl: string) => {
    if (!pdfUrl) return null;

    try {
      const fileName = pdfUrl.split('/').pop();
      const bucket = pdfUrl.includes('lab-reports') ? 'lab-reports' : 'radiology-reports';
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(fileName || '', 3600);

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Failed to get signed URL:', error);
      return null;
    }
  };

  const handleView = async (result: CombinedResult) => {
    const url = await getSignedUrl(result.pdf_url);
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Failed to load report');
    }
  };

  const handleDownload = async (result: CombinedResult) => {
    const url = await getSignedUrl(result.pdf_url);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `${result.report_no}.pdf`;
      link.click();
    } else {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading results...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Patient Results</h1>
        <p className="text-gray-600">View Lab and Radiology reports for your patients</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'all' | 'lab' | 'radiology')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All</option>
              <option value="lab">Lab Only</option>
              <option value="radiology">Radiology Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
            <input
              type="text"
              value={filterPatient}
              onChange={(e) => setFilterPatient(e.target.value)}
              placeholder="Search patient..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Report No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tests</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredResults.map((result) => (
                <tr key={`${result.result_type}-${result.id}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      result.result_type === 'lab'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {result.result_type === 'lab' ? 'Lab' : 'Radiology'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {result.report_no}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {result.patient_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div className="max-w-xs truncate" title={result.tests}>
                      {result.tests}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(result.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      result.status === 'finalized'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {result.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(result)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Report"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(result)}
                        className="text-green-600 hover:text-green-800"
                        title="Download Report"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredResults.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No results found
          </div>
        )}
      </div>
    </div>
  );
}
